<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { ChevronLeft, ChevronRight, Pencil, Plus, Search } from 'lucide-vue-next'
import SectionHead from './SectionHead.vue'
import { filterProducts, paginate, productCategories, topCategory, type ProductSort, type ProfileProduct } from './productList'

const props = defineProps<{ products: ProfileProduct[]; canEdit: boolean }>()

const search = ref('')
const query = ref('')
const category = ref('')
const sort = ref<ProductSort>('az')
const page = ref(1)

let timer: ReturnType<typeof setTimeout> | undefined
watch(search, v => {
  clearTimeout(timer)
  timer = setTimeout(() => { query.value = v }, 200)
})
onBeforeUnmount(() => clearTimeout(timer))
watch([query, category, sort], () => { page.value = 1 })

const categories = computed(() => productCategories(props.products))
const filtered = computed(() => filterProducts(props.products, { query: query.value, category: category.value, sort: sort.value }))
const view = computed(() => paginate(filtered.value, page.value))
</script>

<template>
  <section aria-labelledby="csp-products">
    <SectionHead id="csp-products" title="Products" :count="products.length">
      <template v-if="canEdit">
        <router-link :to="{ name: 'supplier-products' }" class="csp-link inline-flex items-center gap-1"><Pencil :size="13" aria-hidden="true" /> Manage</router-link>
        <router-link :to="{ name: 'supplier-product-create' }" class="csp-link inline-flex items-center gap-1">
          <Plus :size="13" aria-hidden="true" /> Add product
        </router-link>
      </template>
    </SectionHead>

    <div class="flex flex-wrap gap-2 mb-2">
      <label class="relative flex-1 min-w-[180px]">
        <span class="sr-only">Search products</span>
        <Search :size="14" class="absolute left-2.5 top-1/2 -translate-y-1/2 csp-muted" aria-hidden="true" />
        <input v-model="search" type="search" placeholder="Search products" class="csp-input w-full pl-8" />
      </label>
      <label class="flex items-center gap-1.5 text-[13px] csp-muted min-w-0 max-w-full">
        Category:
        <select v-model="category" class="csp-input min-w-0 max-w-full">
          <option value="">All</option>
          <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
        </select>
      </label>
      <label class="flex items-center gap-1.5 text-[13px] csp-muted">
        Sort:
        <select v-model="sort" class="csp-input">
          <option value="az">A–Z</option>
          <option value="za">Z–A</option>
        </select>
      </label>
    </div>

    <ul>
      <li v-for="p in view.rows" :key="p.id" class="csp-row csp-row-link relative flex items-center gap-4 border-b border-(--csp-divider)">
        <div class="min-w-0 flex-1">
          <router-link :to="{ name: 'supplier-product-detail', params: { productId: p.id } }" class="csp-title-link font-semibold after:absolute after:inset-0 after:content-['']">{{ p.name }}</router-link>
          <p class="text-[13px] csp-muted truncate" :title="p.description || ''">{{ p.description || 'No description' }}</p>
        </div>
        <span class="text-[13px] csp-muted shrink-0 text-right max-w-[40%] truncate">{{ topCategory(p) }}</span>
      </li>
      <li v-if="!view.rows.length" class="csp-row flex items-center text-sm csp-muted border-b border-(--csp-divider)">
        {{ products.length ? 'No products match your filters.' : 'No products yet.' }}
      </li>
    </ul>

    <div class="flex items-center justify-between mt-3 text-[13px] csp-muted">
      <span data-test="range">{{ view.from }}–{{ view.to }} of {{ view.total }}</span>
      <div class="flex gap-1">
        <button type="button" class="csp-icon-btn" aria-label="Previous page" :disabled="view.page <= 1" @click="page = view.page - 1">
          <ChevronLeft :size="15" aria-hidden="true" />
        </button>
        <button type="button" class="csp-icon-btn" aria-label="Next page" :disabled="view.page >= view.pages" @click="page = view.page + 1">
          <ChevronRight :size="15" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>
